// src/components/ui/ModulesPanel.tsx
import React from 'react';
import { X, Star, Search, ChevronRight, Grid3X3 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from 'lucide-react';

interface ModuleItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: { text: string; color: string };
  checkAccess?: () => boolean;
}

interface Category {
  id: string;
  title: string;
  icon?: React.ElementType;
  color: string;
  items: ModuleItem[];
}

interface ModulesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  favorites: string[];
  onToggleFavorite: (id: string) => void;
}

export const ModulesPanel: React.FC<ModulesPanelProps> = ({
  isOpen,
  onClose,
  categories,
  favorites,
  onToggleFavorite
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (isOpen) {
      // Rozwiń wszystkie kategorie domyślnie
      setExpandedCategories(new Set(categories.map(c => c.id)));
    }
  }, [isOpen, categories]);

  if (!isOpen) return null;

  // Filtruj moduły na podstawie wyszukiwania
  const filteredCategories = categories.map(category => ({
    ...category,
    items: category.items.filter(item => 
      item.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!item.checkAccess || item.checkAccess())
    )
  })).filter(cat => cat.items.length > 0);

  // Kategorie z ikonami i kolorami - ulepszone
  const categoryStyles: Record<string, { 
    icon: keyof typeof Icons; 
    gradient: string; 
    bgColor: string;
    iconColor: string;
  }> = {
    'ai': { 
      icon: 'Sparkles' as keyof typeof Icons, 
      gradient: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10 hover:bg-purple-500/20',
      iconColor: 'text-purple-400'
    },
    'logistics': { 
      icon: 'Package' as keyof typeof Icons, 
      gradient: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/10 hover:bg-orange-500/20',
      iconColor: 'text-orange-400'
    },
    'finance': { 
      icon: 'TrendingUp' as keyof typeof Icons, 
      gradient: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/10 hover:bg-green-500/20',
      iconColor: 'text-green-400'
    },
    'marketing': { 
      icon: 'Megaphone' as keyof typeof Icons, 
      gradient: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10 hover:bg-blue-500/20',
      iconColor: 'text-blue-400'
    },
    'tools3d': { 
      icon: 'Box' as keyof typeof Icons, 
      gradient: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-500/10 hover:bg-indigo-500/20',
      iconColor: 'text-indigo-400'
    },
    'technical': { 
      icon: 'Settings' as keyof typeof Icons, 
      gradient: 'from-gray-500 to-gray-600',
      bgColor: 'bg-gray-500/10 hover:bg-gray-500/20',
      iconColor: 'text-gray-400'
    },
    'help': { 
      icon: 'HelpCircle' as keyof typeof Icons, 
      gradient: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-500/10 hover:bg-teal-500/20',
      iconColor: 'text-teal-400'
    },
    'admin': { 
      icon: 'Shield' as keyof typeof Icons, 
      gradient: 'from-red-500 to-rose-500',
      bgColor: 'bg-red-500/10 hover:bg-red-500/20',
      iconColor: 'text-red-400'
    }
  };

  const handleModuleClick = (href: string) => {
    navigate(href);
    onClose();
  };

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // Get favorite modules
  const favoriteModules = categories.flatMap(cat => cat.items)
    .filter(item => favorites.includes(item.id));

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-in fade-in duration-200"
        onClick={onClose}
      />
      
      {/* Panel - kompaktowy, po lewej stronie */}
      <div className="fixed left-0 top-0 h-full w-[320px] bg-gray-950 border-r border-gray-800 shadow-2xl z-50 overflow-hidden animate-in slide-in-from-left duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 border-b border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-purple-400" />
              Centrum Modułów
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Szukaj modułu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:bg-gray-800"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-120px)] custom-scrollbar">
          <div className="p-3 space-y-3">
            {/* Ulubione */}
            {favoriteModules.length > 0 && !searchTerm && (
              <div className="pb-3 border-b border-gray-800">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Star className="w-3 h-3 text-yellow-400" />
                  Ulubione
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {favoriteModules.slice(0, 8).map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleModuleClick(item.href)}
                        className={`
                          relative p-2.5 rounded-lg transition-all group
                          ${isActive 
                            ? 'bg-purple-500/20 border border-purple-500/50' 
                            : 'bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50'
                          }
                        `}
                        title={item.label}
                      >
                        <Icon className={`w-5 h-5 mx-auto ${isActive ? 'text-purple-400' : 'text-gray-400 group-hover:text-gray-300'}`} />
                        <span className="text-[10px] text-gray-400 block mt-1 truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Kategorie */}
            {filteredCategories.map(category => {
              const categoryStyle = categoryStyles[category.id] || {
                icon: 'Folder' as keyof typeof Icons,
                gradient: 'from-gray-500 to-gray-600',
                bgColor: 'bg-gray-500/10 hover:bg-gray-500/20',
                iconColor: 'text-gray-400'
              };
              const CategoryIcon = Icons[categoryStyle.icon] || Icons.Folder;
              const isExpanded = expandedCategories.has(category.id) || searchTerm;
              
              return (
                <div key={category.id} className="space-y-2">
                  {/* Nagłówek kategorii */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`
                      w-full flex items-center justify-between p-2.5 rounded-lg transition-all
                      ${categoryStyle.bgColor} group
                    `}
                  >
                    <div className="flex items-center gap-2.5">
                      <CategoryIcon className={`w-4 h-4 ${categoryStyle.iconColor}`} />
                      <span className="font-medium text-sm text-gray-200">{category.title}</span>
                      <span className="text-xs text-gray-500">({category.items.length})</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </button>

                  {/* Moduły w kategorii - grid 3 kolumny */}
                  {isExpanded && (
                    <div className="grid grid-cols-3 gap-1.5 pl-2">
                      {category.items.map(item => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.href;
                        const isFavorite = favorites.includes(item.id);
                        
                        return (
                          <div key={item.id} className="relative group">
                            <button
                              onClick={() => handleModuleClick(item.href)}
                              className={`
                                w-full p-2 rounded-lg transition-all flex flex-col items-center gap-1
                                ${isActive 
                                  ? `bg-gradient-to-r ${categoryStyle.gradient} text-white shadow-lg` 
                                  : 'bg-gray-800/30 hover:bg-gray-800/60 text-gray-400 hover:text-gray-200'
                                }
                              `}
                              title={item.label}
                            >
                              <Icon className="w-5 h-5" />
                              <span className="text-[10px] text-center leading-tight truncate w-full">{item.label}</span>
                              {item.badge && (
                                <span className={`absolute -top-1 -right-1 px-1 py-0.5 text-[8px] rounded ${item.badge.color} text-white font-bold`}>
                                  {item.badge.text}
                                </span>
                              )}
                            </button>
                            
                            {/* Favorite button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorite(item.id);
                              }}
                              className="absolute top-0.5 right-0.5 p-0.5 bg-gray-900/80 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Star className={`w-2.5 h-2.5 ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-500'}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-gray-900 to-transparent">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>ESC - zamknij</span>
            <kbd className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] border border-gray-700">⌘M</kbd>
          </div>
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(100, 100, 100, 0.3);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 100, 100, 0.5);
        }
      `}</style>
    </>
  );
};
